class HeaderSearchDesktop extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.overlay = this.querySelector('[data-search-overlay]');
    this.panel = this.querySelector('[data-search-panel]');
    this.backdrop = this.querySelector('[data-search-backdrop]');
    this.predictiveSearch = this.querySelector('predictive-search, search-form');

    if (!this.input || !this.overlay) return;

    this.bindPredictiveSearchResults();

    this.onInput = this.onInput.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onReset = this.onReset.bind(this);
    this.onResize = this.onResize.bind(this);
    this.updateHeaderPosition = this.updateHeaderPosition.bind(this);

    this.input.addEventListener('focus', () => this.open());
    this.input.addEventListener('click', (event) => {
      event.stopPropagation();
      this.open();
    });
    this.input.addEventListener('input', this.onInput);

    this.form?.addEventListener('reset', this.onReset);
    this.backdrop?.addEventListener('click', () => this.close(true));
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);

    this.syncQueryState();
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    this.form?.removeEventListener('reset', this.onReset);
    window.removeEventListener('resize', this.updateHeaderPosition);
  }

  bindPredictiveSearchResults() {
    if (!this.predictiveSearch || !this.panel) return;

    const resultsContainer = this.panel.querySelector('[data-predictive-search]');
    if (resultsContainer) {
      this.predictiveSearch.predictiveSearchResults = resultsContainer;
    }
  }

  onResize() {
    if (window.innerWidth < 990 && this.isOpen) {
      this.close(true);
      return;
    }

    if (this.isOpen) {
      this.updateHeaderPosition();
    }
  }

  onInput() {
    this.syncQueryState();
  }

  syncQueryState() {
    const hasQuery = this.input.value.trim().length > 0;
    this.classList.toggle('has-query', hasQuery);
    this.overlay?.classList.toggle('has-query', hasQuery);

    if (hasQuery && this.predictiveSearch?.onChange && this.isOpen) {
      this.predictiveSearch.onChange();
    }
  }

  onReset() {
    window.setTimeout(() => {
      this.classList.remove('has-query');
      this.overlay?.classList.remove('has-query');
    }, 0);
  }

  onDocumentPointerDown(event) {
    if (!this.isOpen) return;

    const target = event.target;
    if (this.contains(target) || this.overlay?.contains(target)) return;

    this.close(true);
  }

  onKeydown(event) {
    if (event.code === 'Escape' && this.isOpen) {
      this.close(true);
    }
  }

  open() {
    if (window.innerWidth < 990) return;

    this.bindPredictiveSearchResults();
    this.updateHeaderPosition();

    this.isOpen = true;
    this.classList.add('is-open');
    this.overlay.hidden = false;

    document.body.classList.add('overflow-hidden');

    if (this.predictiveSearch) {
      this.predictiveSearch.isOpen = true;
    }

    this.syncQueryState();
    window.addEventListener('resize', this.updateHeaderPosition);
  }

  updateHeaderPosition() {
    const header = document.querySelector('.section-header');
    if (!header) return;

    const borderOffset = header.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom')
      ? 1
      : 0;

    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(header.getBoundingClientRect().bottom - borderOffset, 10)}px`
    );
  }

  close(clearSearchTerm = false) {
    if (!this.isOpen && !clearSearchTerm) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    this.overlay.hidden = true;
    this.overlay?.classList.remove('has-query');
    document.body.classList.remove('overflow-hidden');
    window.removeEventListener('resize', this.updateHeaderPosition);

    if (clearSearchTerm) {
      if (this.predictiveSearch?.close) {
        this.predictiveSearch.close(true);
      } else if (this.input) {
        this.input.value = '';
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.classList.remove('has-query');
      this.input?.blur();
    } else if (this.predictiveSearch) {
      this.predictiveSearch.closeResults?.(false);
      this.predictiveSearch.isOpen = false;
    }
  }
}

customElements.define('header-search-desktop', HeaderSearchDesktop);
